import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { CustomFieldOptionType, CustomFieldType } from './CustomFieldTypes';
import TextInput from '../basefields/TextInput';
import Dropdown from '../basefields/Dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';

interface CustomFieldOptionsEditorProps {
    field: CustomFieldType
    updateField: (updates: Partial<CustomFieldType>) => void
}

// The standard colors an option can get, the same set as the label colors
const OPTION_COLORS = [
    { value: 'background-dark-blue', color: 'background-dark-blue' },
    { value: 'background-blue', color: 'background-blue' },
    { value: 'background-dark-green', color: 'background-dark-green' },
    { value: 'background-green', color: 'background-green' },
    { value: 'background-yellow', color: 'background-yellow' },
    { value: 'background-orange', color: 'background-orange' },
    { value: 'background-red', color: 'background-red' },
    { value: 'background-gray', color: 'background-gray' },
];

const CustomFieldOptionsEditor: React.FC<CustomFieldOptionsEditorProps> = ({ field, updateField }) => {
    const { t } = useTranslation();

    // Only render the non deleted options
    const visibleOptions = (field.options || []).filter(custom_field_option => !custom_field_option.deleted);

    // Update one option by its identifier, so deleted options never shift the index
    const handleUpdateOption = (identifier: string | number, updates: Partial<CustomFieldOptionType>) => {
        const updatedOptions = (field.options || []).map(custom_field_option => (custom_field_option.id || custom_field_option.uuid) === identifier
            ? { ...custom_field_option, ...updates }
            : custom_field_option
        );

        updateField({ options: updatedOptions });
    };

    // Add a new option
    const handleAddOption = () => {
        const newOption: CustomFieldOptionType = {
            uuid: uuidv4(),
            label: '',
            value: '',
            ordering: field.options?.length || 0,
        }

        updateField({options: [ ...(field.options || []), newOption ]})
    };

    // Delete an option
    const handleDeleteOption = (identifier: string | number) => {
        const updatedOptions = (field.options || []).map(custom_field_option => (custom_field_option.id || custom_field_option.uuid) === identifier
            ? { ...custom_field_option, deleted: true }
            : custom_field_option
        );

        updateField({ options: updatedOptions });
    };

    const handleReordering = (result: DropResult) => {
        if (result.destination) {
            const reorderedOptions = [...visibleOptions];

            const [removedOption] = reorderedOptions.splice(result.source.index, 1);

            reorderedOptions.splice(result.destination.index, 0, removedOption);

            const updatedVisibleOptions = reorderedOptions.map((option, index) => ({
                ...option,
                ordering: index + 1,
            }));

            const deletedOptions = (field.options || []).filter(custom_field_option => custom_field_option.deleted);

            updateField({ options: [ ...updatedVisibleOptions, ...deletedOptions ]});
        }
    };

    return (
        <div className='dropdown-options-editor'>
            <label>
                {t('custom_field.general.enter_options_label')}
            </label>

            <DragDropContext onDragEnd={handleReordering}>
                <StrictModeDroppable droppableId='dropdown-options'>
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className='dropdown-options'
                        >
                            {visibleOptions.map((option, index) => (
                                <Draggable
                                    key={option.id || option.uuid}
                                    draggableId={`option-${option.id || option.uuid}`}
                                    index={index}
                                >
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className='dropdown-option-row'
                                        >
                                            <TextInput
                                                name='option'
                                                value={option.label}
                                                placeholder='custom_field.general.type_option'
                                                onChange={(value) => {
                                                    handleUpdateOption(option.id || option.uuid || '', {
                                                        label: value,
                                                        value: value
                                                            .toLowerCase()
                                                            .trim()
                                                            .replace(/[^a-z0-9]+/g, '_')
                                                            .replace(/^_+|_+$/g, ''),
                                                    });
                                                }}
                                            />

                                            <div className='color-placeholder'>
                                                <Dropdown<any>
                                                    name='option-color'
                                                    options={OPTION_COLORS}
                                                    selectedOption={OPTION_COLORS.find(optionColor => optionColor.value === option.color) ?? null}
                                                    optionMode='dot'
                                                    optionFormat='value'
                                                    backendField='value'
                                                    showSearch={false}
                                                    allowNoneOption={true}
                                                    noneOptionLabel='custom_field.general.option_no_color_label'
                                                    disabled_selected='custom_field.general.option_color_placeholder'
                                                    fixedDropdownWidth={90}
                                                    onChange={(value) => {
                                                        handleUpdateOption(option.id || option.uuid || '', {
                                                            color: value === 'none' ? null : value,
                                                        });
                                                    }}
                                                />
                                            </div>

                                            <div className='delete-placeholder'>
                                                <div className='delete-icon tooltip-icon'>
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                        onClick={() => handleDeleteOption(option.id || option.uuid || '')}
                                                    />
                                                    <span className="tooltip">
                                                        {t('general.delete')}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className='order-placeholder'>
                                                {visibleOptions.length > 1 && (
                                                    <div
                                                        className='order-icon tooltip-icon'
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <FontAwesomeIcon icon={faGripLines} />
                                                        <span className="tooltip">
                                                            {t('general.reorder')}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </StrictModeDroppable>
            </DragDropContext>
            <div onClick={(event) => {event.preventDefault(); handleAddOption()}}
                 className="add-new-button">
                {t('custom_field.general.add_option_label')}
            </div>
        </div>
    );
}

export default CustomFieldOptionsEditor